import { zodResolver } from '@hookform/resolvers/zod';
import { Grid, TextField, TextFieldProps, Checkbox, FormControlLabel } from '@mui/material';
import { ConfirmationDialogContentHandle } from '@procureai/ui-mui';
import { PropsWithChildren, forwardRef, useImperativeHandle } from 'react';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DemandAwardingSettings } from '../../DemandAwardingWidget.types';
import { I18nNamespace } from '~/common/types';
import { useFormFieldErrorTranslation } from '~/hooks';

import { messageFormDataSchema, MessageFormData } from './MessageForm.schema';

const commonInputProps: Pick<TextFieldProps<'outlined'>, 'margin' | 'fullWidth' | 'variant' | 'size'> = {
  margin: 'dense',
  fullWidth: true,
  variant: 'outlined',
  size: 'small',
};

export interface MessageFormProps {
  i18nKeyPrefix: string;
  numCases: number;
  awardingSettings: DemandAwardingSettings;
  onConfirm?: (data: MessageFormData) => void;
  showDismissOtherSuppliers?: boolean;
}

export const MessageForm = forwardRef<ConfirmationDialogContentHandle, PropsWithChildren<MessageFormProps>>(
  function CommentDialogForm(
    { onConfirm, i18nKeyPrefix, numCases, awardingSettings, showDismissOtherSuppliers = true },
    ref,
  ) {
    const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: i18nKeyPrefix });
    const { errorToHelperText } = useFormFieldErrorTranslation();

    const { options } = awardingSettings.notifications;

    const form = useForm<MessageFormData>({
      mode: 'onTouched',
      resolver: zodResolver(messageFormDataSchema),
      defaultValues: {
        dismissOtherSuppliers: awardingSettings.notifications.options.dismissOtherSuppliers.defaultValue,
        informDismissedSuppliers: awardingSettings.notifications.options.informDismissedSupplier.defaultValue,
        informAwardedSupplier: awardingSettings.notifications.options.informAwardedSupplier.defaultValue,
      },
    });

    const handleSubmit = form.handleSubmit((data) => {
      if (onConfirm) {
        onConfirm(data);
      }
    });

    useImperativeHandle(ref, () => ({
      confirm: handleSubmit,
    }));

    return (
      <FormProvider {...form}>
        <form onSubmit={handleSubmit} autoComplete="off" noValidate>
          <Grid
            container
            direction="row"
            spacing={1}
            sx={{
              justifyContent: 'flex-start',
              alignContent: 'flex-start',
            }}
          >
            <Grid size={{ xs: 12, md: 12 }}>{t('description')}</Grid>
            {options.informSupplier && (
              <>
                {numCases > 1 && (
                  <>
                    {showDismissOtherSuppliers && options.dismissOtherSuppliers.show && (
                      <Grid size={{ xs: 12, md: 12 }}>
                        <Controller
                          control={form.control}
                          name="dismissOtherSuppliers"
                          render={({ field }) => (
                            <FormControlLabel
                              label={t('dismissOtherSuppliers')}
                              control={
                                <Checkbox checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
                              }
                            />
                          )}
                        />
                      </Grid>
                    )}
                    {options.informDismissedSupplier.show && (
                      <Grid size={{ xs: 12, md: 12 }}>
                        <Controller
                          control={form.control}
                          name="informDismissedSuppliers"
                          render={({ field }) => (
                            <FormControlLabel
                              label={t('informDismissedSuppliers')}
                              control={
                                <Checkbox checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
                              }
                            />
                          )}
                        />
                      </Grid>
                    )}
                  </>
                )}
                {options.informAwardedSupplier.show && (
                  <>
                    <Grid size={{ xs: 12, md: 12 }}>
                      <Controller
                        control={form.control}
                        name="informAwardedSupplier"
                        render={({ field }) => (
                          <FormControlLabel
                            label={t('informAwardedSupplier')}
                            control={
                              <Checkbox checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
                            }
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 12 }}>
                      <Controller
                        control={form.control}
                        name="message"
                        defaultValue=""
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            label={t('message')}
                            error={!!error}
                            helperText={errorToHelperText(error)}
                            multiline
                            minRows={4}
                            maxRows={12}
                            {...commonInputProps}
                            {...field}
                            value={field.value || ''}
                          />
                        )}
                      />
                    </Grid>
                  </>
                )}
              </>
            )}
          </Grid>
        </form>
      </FormProvider>
    );
  },
);
